import type { StateStorage } from 'zustand/middleware';

/** The key the original app used, and the one we keep writing to. */
export const STORAGE_KEY = 'jdc_anso_v2';

/**
 * Storage adapter that can read what the previous version left behind.
 *
 * The original app wrote its state object directly:
 *
 *     localStorage.setItem('jdc_anso_v2', JSON.stringify(data))
 *
 * whereas zustand's `persist` expects an envelope, `{ state, version }`. Read
 * naively, a legacy save produces `state === undefined` and the migration
 * receives nothing — silently wiping a school year of work on the first
 * launch of the new version.
 *
 * So on read we detect the bare shape and wrap it at version 0, which routes
 * it through the normal migration path.
 */
function looksLikeLegacySave(parsed: unknown): boolean {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
  const r = parsed as Record<string, unknown>;

  // A zustand envelope always carries `state`; a legacy save never does, but
  // it does carry the collections the app has always had.
  if ('state' in r) return false;
  return 'lessons' in r || 'students' in r || 'settings' in r;
}

export function createLegacyAwareStorage(backing: Storage): StateStorage {
  return {
    getItem: (name) => {
      const raw = backing.getItem(name);
      if (raw === null) return null;

      try {
        const parsed: unknown = JSON.parse(raw);
        if (looksLikeLegacySave(parsed)) {
          return JSON.stringify({ state: parsed, version: 0 });
        }
      } catch {
        // Unparseable: hand it back untouched and let persist fall back to
        // defaults rather than throwing during startup.
        return raw;
      }

      return raw;
    },

    setItem: (name, value) => {
      backing.setItem(name, value);
    },

    removeItem: (name) => {
      backing.removeItem(name);
    },
  };
}
